import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { UserProfileStore } from '../stores/user-profile.store';
import { NotificationService } from '../providers/notification.service';
import { ErrorStateService } from '../providers/error-state.service';
import { Observable, catchError, filter, of, switchMap, timeout } from 'rxjs';
import { UserProfileViewModel } from '../models/main/user-profile.model';


@Injectable()
export class AuthGuard  {

    private readonly POLICY_ADMIN: string = "Admin";
    private readonly TIMEOUT_DURATION: number = 10000; // Timeout duration in milliseconds, e.g., 10 seconds

    constructor(
        private readonly router: Router,
        private readonly userProfileStore: UserProfileStore,
        private readonly notificationService: NotificationService,
        private readonly errorStateService: ErrorStateService) {
    }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> | boolean {
        const userProfile = this.userProfileStore.getUserProfile();

        if (userProfile) {
            return this.handleUserProfile(userProfile, route);
        } else {
            return this.userProfileStore.userProfile$.pipe(
                filter(userProfile => userProfile !== null), // Ignore null values
                timeout(this.TIMEOUT_DURATION), // Timeout after specified duration
                switchMap((profile) => {
                    if (profile) {
                        return of(this.handleUserProfile(profile, route));
                    } else {
                        this.router.navigate(['/no-permissions']);
                        return of(false);
                    }
                }),
                catchError((error) => {
                        // El perfil no llegó (timeout): normalmente la carga de
                        // /settings/user falló por sesión inválida. Dar contexto a
                        // la página de error en vez de un genérico.
                        this.errorStateService.setError({
                            code: '401',
                            message: 'No se pudo cargar el perfil de usuario',
                            details: error?.name === 'TimeoutError'
                                ? 'Timeout esperando el perfil de usuario'
                                : error?.message,
                        });
                        this.router.navigate(['/error']);
                        return of(false);
                })
            );
        }
    }

    private handleUserProfile(userProfile: UserProfileViewModel, route: ActivatedRouteSnapshot): boolean {
        const userProfilePolicies = userProfile?.profiles?.map(x => x.profile);

        if (!userProfilePolicies || userProfilePolicies.length == 0) {
            this.router.navigate(['/no-permissions'])
        }

        // Admin clause
        if (userProfilePolicies.filter(x => x == this.POLICY_ADMIN).length > 0) {
            return true;
        }

        // Service validation
        let result = true;

        // Policy validation
        const policies = route.data["policies"];

        if (policies && policies.length > 0) {
            const brokenPolicies = policies.filter(p => !userProfilePolicies.includes(p));

            if (brokenPolicies && brokenPolicies.length > 0) {
                this.notificationService.showError(`[ACCESS DENIED] Broken policies: ${  brokenPolicies.toString()}`);
            }
            result = result && brokenPolicies.length == 0;
        }

        // Guard results behavior
        if (!result) {
            this.router.navigate(['/home'])
        }
        return result;
    }
}
